import { useReducer, useMemo, useCallback } from 'react';
import { MRT_RowSelectionState, MRT_Row } from 'material-react-table';
import { UseCustomRowSelectionProps, UseCustomRowSelectionReturn } from './types';
import { selectionReducer, initialState } from './helpers';

/**
 * Hook to manage custom row selection in Material React Table.
 *
 * Supports selecting individual rows and all rows across all pages,
 * even when data is paginated and loaded asynchronously.
 * Uses a reducer so selection state updates atomically and totalSelectedRows stays in sync.
 */
export const useCustomRowSelection = ({
    totalRows,
    data = [],
    onSelectionChange,
}: UseCustomRowSelectionProps): UseCustomRowSelectionReturn => {
    const [state, dispatch] = useReducer(selectionReducer, initialState);
    const { selectedRows, allRowsSelected } = state;

    const totalSelectedRows = useMemo(
        () => allRowsSelected ? totalRows : Object.keys(selectedRows).length,
        [allRowsSelected, selectedRows, totalRows]
    );

    const handleRowToggle = useCallback((rowId: string, checked: boolean) => {
        dispatch({ type: 'ROW_TOGGLE', rowId, checked });
    }, []);

    const handleSelectAll = useCallback(
        (checked: boolean, currentPageRows?: MRT_Row<any>[]) => {
            if (checked) {
                const rows =
                    currentPageRows ||
                    (data?.map((item, index) => ({ id: item.id ?? index, original: item } as MRT_Row<any>)) ?? []);
                const selection: Record<string, boolean> = {};
                rows.forEach((row) => {
                    selection[String(row.id)] = true;
                });
                dispatch({ type: 'SELECT_ALL', checked: true, selection });
            } else {
                dispatch({ type: 'SELECT_ALL', checked: false, selection: {} });
            }
        },
        [data]
    );

    const resetSelection = useCallback(() => {
        dispatch({ type: 'RESET' });
        if (onSelectionChange) {
            onSelectionChange({}, false);
        }
    }, [onSelectionChange]);

    const setSelectedRows = useCallback((rows: MRT_RowSelectionState) => {
        dispatch({ type: 'SET_SELECTED_ROWS', payload: rows });
    }, []);

    const setAllRowsSelected = useCallback((value: boolean) => {
        dispatch({ type: 'SET_ALL_ROWS_SELECTED', value });
    }, []);

    return {
        selectedRows,
        allRowsSelected,
        totalSelectedRows,
        handleRowToggle,
        handleSelectAll,
        resetSelection,
        setSelectedRows,
        setAllRowsSelected,
    };
};
